import store from './store/index';
import observeIntersection from './inc/observeIntersection';

/* eslint-disable-next-line */
const Vue = require('vue');

const {
    ref, onMounted, computed, watch, nextTick
} = Vue;

// Header Funktions
const headerFunctions = {
    name: 'HeaderFunctions',
    setup() {
        const ready = ref(false);
        const menuState = computed(() => store.getters.getMenuState);
        const magazineCat = computed(() => store.getters.getMagazineCat);
        const filteredPosts = computed(() => store.getters.getFilteredPosts);
        const postCount = computed(() => store.getters.getPostCount);
        const prevPost = computed(() => store.getters.getPrevPost);
        const nextPost = computed(() => store.getters.getNextPost);
        const startPage = computed(() => store.getters.getStartPage);
        const currentIdInMagazine = computed(() => store.getters.getCurrentIdInMagazine);
        /* eslint-disable-next-line */
        const { isKiosk } = wpUrls;

        onMounted(async() => {
            store.dispatch('setMagazineCat');
            await store.dispatch('setPosts');
            await store.dispatch('setPages');
            store.dispatch('setPagination');
            store.dispatch('getCurrentIdInMagazine');

            if (parseInt(isKiosk, 10) !== 1) {
                ready.value = true;
            }
        });

        const setMenuState = () => {
            store.dispatch('setMenuState', true);
        };

        return {
            ready,
            setMenuState,
            menuState,
            filteredPosts,
            magazineCat,
            postCount,
            prevPost,
            nextPost,
            startPage,
            currentIdInMagazine,
        };
    }
};
Vue.createApp(headerFunctions).use(store).mount('#headerNav');

// Menu
const magazineMenu = {
    name: 'MagazineMenu',
    setup() {
        const menuState = computed(() => store.getters.getMenuState);
        const filteredPosts = computed(() => store.getters.getFilteredPosts);
        const startPage = computed(() => store.getters.getStartPage);
        const currentIdInMagazine = computed(() => store.getters.getCurrentIdInMagazine);
        const magazineCat = computed(() => store.getters.getMagazineCat);
        const manuelOverridePosts = computed(() => store.getters.getManuelOverridePosts);
        const ready = ref(false);
        const closeButtonRef = ref(null);
        // eslint-disable-next-line no-unused-vars
        const menuItemsRef = ref([]);
        /* eslint-disable-next-line */
        const { isKiosk, magazinColors } = wpUrls;

        onMounted(() => {
            if (parseInt(isKiosk, 10) !== 1) {
                ready.value = true;
            }

            // Erweiterte Tastaturnavigation
            document.addEventListener('keydown', (event) => {
                if (!menuState.value) return;

                // eslint-disable-next-line default-case
                switch (event.key) {
                case 'Escape':
                    closeMenu();
                    break;
                case 'Tab':
                    handleTabNavigation(event);
                    break;
                case 'ArrowDown':
                case 'ArrowUp':
                    event.preventDefault();
                    handleArrowNavigation(event.key);
                    break;
                case 'Home':
                    event.preventDefault();
                    focusFirstMenuItem();
                    break;
                case 'End':
                    event.preventDefault();
                    focusLastMenuItem();
                    break;
                }
            });
        });

        watch(magazineCat, (newCategoryId) => {
            if (newCategoryId) {
                store.dispatch('setMagazineCatData', newCategoryId);
            } else {
                store.dispatch('setManuelOverridePosts');
            }
        });

        // Fokus-Management beim Öffnen des Menüs
        watch(menuState, (newState) => {
            if (newState) {
                // Fokus auf Schließen-Button setzen
                nextTick(() => {
                    if (closeButtonRef.value) {
                        closeButtonRef.value.focus();
                    }
                });
            } else {
                // Fokus zurück zum Hamburger-Menü setzen
                const hamburgerButton = document.querySelector('.header__hambuger');
                if (hamburgerButton) {
                    hamburgerButton.focus();
                }
            }
        });

        const handleTabNavigation = (event) => {
            const focusableElements = getFocusableElements();
            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            if (event.shiftKey && document.activeElement === firstElement) {
                event.preventDefault();
                lastElement.focus();
            } else if (!event.shiftKey && document.activeElement === lastElement) {
                event.preventDefault();
                firstElement.focus();
            }
        };

        const handleArrowNavigation = (key) => {
            const menuItems = getMenuItems();
            const currentIndex = menuItems.findIndex((item) => item === document.activeElement);

            if (currentIndex === -1) {
                focusFirstMenuItem();
                return;
            }

            let nextIndex;
            if (key === 'ArrowDown') {
                nextIndex = currentIndex < menuItems.length - 1 ? currentIndex + 1 : 0;
            } else {
                nextIndex = currentIndex > 0 ? currentIndex - 1 : menuItems.length - 1;
            }

            menuItems[nextIndex].focus();
        };

        const focusFirstMenuItem = () => {
            const menuItems = getMenuItems();
            if (menuItems.length > 0) {
                menuItems[0].focus();
            }
        };

        const focusLastMenuItem = () => {
            const menuItems = getMenuItems();
            if (menuItems.length > 0) {
                menuItems[menuItems.length - 1].focus();
            }
        };

        const getFocusableElements = () => {
            const container = document.querySelector('.magazinList__posts__container');
            if (!container) return [];

            return Array.from(container.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            )).filter((el) => !el.disabled && el.offsetParent !== null);
        };

        const getMenuItems = () => Array.from(document.querySelectorAll('.magazinList__posts a[role="menuitem"]'));

        const randomColor = () => (
            (magazinColors) ? magazinColors[Math.floor(Math.random() * magazinColors.length)] : false
        );

        const closeMenu = () => {
            store.dispatch('setMenuState', false);
        };

        return {
            filteredPosts,
            ready,
            menuState,
            closeMenu,
            startPage,
            currentIdInMagazine,
            magazineCat,
            manuelOverridePosts,
            randomColor,
            closeButtonRef,
        };
    }
};
Vue.createApp(magazineMenu).use(store).mount('#magazinList');

// Footer Pagination
const footerPagination = {
    name: 'FooterPagination',
    setup() {
        const ready = ref(false);
        const prevPost = computed(() => store.getters.getPrevPost);
        const nextPost = computed(() => store.getters.getNextPost);
        const currentIdInMagazine = computed(() => store.getters.getCurrentIdInMagazine);
        const magazineCat = computed(() => store.getters.getMagazineCat);
        const prevHover = ref(false);
        const nextHover = ref(false);
        /* eslint-disable-next-line */
        const { isKiosk, footerPaginationColored, footerPaginationColor, footerPaginationArrowColor, footerPaginationHoverColor } = wpUrls;
        const footerPaginationColorVar = footerPaginationColored && footerPaginationColor
            ? `:root {
                --footerPagination__bgColor: ${footerPaginationColor};
                --footerPagination__arrowColor: ${footerPaginationArrowColor};
                --footerPagination__hoverColor: ${footerPaginationHoverColor};
            }`
            : '';

        onMounted(() => {
            if (parseInt(isKiosk, 10) !== 1) {
                ready.value = true;
            }

            if (footerPaginationColorVar) {
                const style = document.createElement('style');
                const head = document.head || document.getElementsByTagName('head')[0];

                head.appendChild(style);
                style.appendChild(document.createTextNode(footerPaginationColorVar));
            }
        });

        return {
            ready,
            prevPost,
            nextPost,
            currentIdInMagazine,
            magazineCat,
            footerPaginationColorVar,
            prevHover,
            nextHover,
        };
    }
};
Vue.createApp(footerPagination).use(store).mount('#footerPagination');

// Fixed Pagination
const fixedPagination = {
    name: 'FixedPagination',
    setup() {
        const ready = ref(false);
        const prevPost = computed(() => store.getters.getPrevPost);
        const nextPost = computed(() => store.getters.getNextPost);
        const currentIdInMagazine = computed(() => store.getters.getCurrentIdInMagazine);
        const magazineCat = computed(() => store.getters.getMagazineCat);
        const isDark = ref(false);
        const contrast = ref(false);
        const isCustom = ref(false);
        const prevColor = ref(false);
        const nextColor = ref(false);
        /* eslint-disable-next-line */
        const { isKiosk, magazinColors } = wpUrls;

        onMounted(() => {
            if (!document.querySelectorAll('#bigHeaderWrapper.hasMedia').length) {
                isDark.value = true;
            }

            if (magazinColors) {
                prevColor.value = randomColor().color;
                nextColor.value = randomColor().color;
            }

            observeIntersection('#bigHeaderWrapper.hasMedia', (e, intersected) => {
                if (intersected) {
                    isDark.value = false;
                } else {
                    isDark.value = true;
                }
            }, false, 0, undefined, '-90% 0% -10% 0%');

            /* Full WIdth Img Observer ++++++++++++++++++++++++++++++++++++++++++*/
            observeIntersection(
                '.wp-block-image.alignfull, .wp-block-getwid-image-hotspot.alignfull, .parallaxBackground',
                (e, intersected) => {
                    if (intersected) {
                        isDark.value = false;
                    } else {
                        isDark.value = true;
                    }
                },
                false,
                0,
                undefined,
                '-90% 0% -10% 0%'
            );

            observeIntersection(
                '.contentTeaser',
                (e, intersected) => {
                    if (!intersected) {
                        contrast.value = false;
                    } else {
                        contrast.value = true;
                    }
                },
                false,
                0,
                undefined,
                '-90% 0% -10% 0%'
            );

            observeIntersection('#footerPagination, .footer, #newsletter', (e, intersected) => {
                if (intersected) {
                    ready.value = false;
                } else {
                    ready.value = true;
                }
            }, false, 0, undefined, '0px 0px -64px 0px');

            if (parseInt(isKiosk, 10) !== 1) {
                ready.value = true;
            }
        });

        observeIntersection(
            '.fixedNavStyleTrigger',
            (e, intersected) => {
                if (intersected) {
                    isCustom.value = true;
                    isDark.value = false;
                } else {
                    isCustom.value = false;
                    isDark.value = true;
                }
            },
            false,
            0,
            undefined,
            '-90% 0% -10% 0%'
        );

        const randomColor = () => (
            (magazinColors) ? magazinColors[Math.floor(Math.random() * magazinColors.length)] : false
        );

        return {
            ready,
            isDark,
            contrast,
            isCustom,
            prevPost,
            nextPost,
            currentIdInMagazine,
            magazineCat,
            randomColor,
            nextColor,
            prevColor,
        };
    }
};
Vue.createApp(fixedPagination).use(store).mount('#fixedPagination');

// Posts Index
if (document.getElementById('postsIndex')) {
    const postsIndex = {
        name: 'PostsIndex',
        setup() {
            const ready = ref(false);
            const filteredPosts = computed(() => store.getters.getFilteredPosts);
            const startPage = computed(() => store.getters.getStartPage);
            const indexPosts = ref([]);
            /* eslint-disable-next-line */
            const { isKiosk, magazinColors } = wpUrls;

            const randomColor = () => (
                (magazinColors) ? magazinColors[Math.floor(Math.random() * magazinColors.length)] : false
            );

            watch(filteredPosts, () => {
                if (parseInt(isKiosk, 10) !== 1) {
                    ready.value = true;
                }

                if (
                    window.excludePosts !== null
                        && typeof window.excludePosts !== 'undefined'
                        && window.excludePosts !== ''
                ) {
                    indexPosts.value = filteredPosts.value.filter((post) => !window.excludePosts.includes(post.id));
                } else {
                    indexPosts.value = filteredPosts.value;
                }
            });

            return {
                startPage,
                ready,
                indexPosts,
                randomColor,
            };
        }
    };

    Vue.createApp(postsIndex).use(store).mount('#postsIndex');
}

// Header Logo
const headerLogo = {
    name: 'HeaderLogo',
    setup() {
        const ready = ref(false);
        const startPage = computed(() => store.getters.getStartPage);
        const startPageUrl = ref('void:javascript;');
        /* eslint-disable-next-line */
        const { isKiosk, logoLink } = wpUrls;

        watch(startPage, () => {
            if (parseInt(isKiosk, 10) !== 1) {
                ready.value = true;
            }

            if (logoLink) { return; }
            startPageUrl.value = startPage.value.permalink;
        });

        onMounted(() => {
            if (logoLink) { startPageUrl.value = logoLink; }
        });

        return {
            startPage,
            ready,
            startPageUrl,
        };
    }
};
Vue.createApp(headerLogo).use(store).mount('#headerLogo');

// Header Button
const headerButton = {
    name: 'HeaderButton',
    setup() {
        const ready = ref(false);
        const magazineCat = computed(() => store.getters.getMagazineCat);
        const magazineCatData = computed(() => store.getters.getMagazineCatData);
        const buttonData = ref(null);
        const isLoading = ref(false);
        const themeTag = ref('');
        /* eslint-disable-next-line */
        const { isKiosk } = wpUrls;

        const fetchAndUpdateButton = async(categoryId) => {
            if (!categoryId || isLoading.value) return;

            isLoading.value = true;
            try {
                const categoryData = await store.dispatch('setMagazineCatData', categoryId);
                if (categoryData && categoryData.acf && categoryData.acf.headerbuttonmagazinecat) {
                    buttonData.value = categoryData.acf.headerbuttonmagazinecat;
                } else {
                    // Keep buttonData as null to show PHP fallback
                    buttonData.value = null;
                }
            } catch (error) {
                // On error, keep buttonData as null to show PHP fallback
                buttonData.value = null;
            } finally {
                isLoading.value = false;
            }
        };

        watch(magazineCat, (newCategoryId) => {
            if (newCategoryId) {
                fetchAndUpdateButton(newCategoryId);
                ready.value = true;
            }
        });

        watch(magazineCatData, (newCategoryData) => {
            if (newCategoryData && newCategoryData.acf && newCategoryData.acf.headerbuttonmagazinecat) {
                buttonData.value = newCategoryData.acf.headerbuttonmagazinecat;
            } else {
                buttonData.value = null;
            }
        });

        onMounted(() => {
            // Check for theme tag
            themeTag.value = document.body.className.includes('birkenstock') ? ' arrowUp' : '';

            if (magazineCat.value) {
                fetchAndUpdateButton(magazineCat.value);
                ready.value = true;
            }
        });

        // eslint-disable-next-line
        const buttonTarget = computed(() => {
            return buttonData.value && buttonData.value.target ? buttonData.value.target : null;
        });

        // eslint-disable-next-line
        const buttonClass = computed(() => {
            // eslint-disable-next-line
            return 'btn header__button' + themeTag.value;
        });

        // eslint-disable-next-line
        const buttonAriaLabel = computed(() => {
            // eslint-disable-next-line
            return buttonData.value ? 'Zur Seite ' + buttonData.value.title : '';
        });

        // eslint-disable-next-line
        const showVueButton = computed(() => {
            return ready.value && buttonData.value && buttonData.value.url && buttonData.value.title;
        });

        return {
            ready,
            buttonData,
            isLoading,
            magazineCat,
            themeTag,
            buttonTarget,
            buttonClass,
            buttonAriaLabel,
            showVueButton,
        };
    }
};
Vue.createApp(headerButton).use(store).mount('#headerButton');

// Kiosk Search
if (document.getElementById('kioskSearch')) {
    const kioskSearch = {
        name: 'KioskSearch',
        setup() {
            const ready = ref(false);
            const pages = computed(() => store.getters.getPages);
            const posts = computed(() => store.getters.getPosts);
            const magazinSearch = ref('');
            const filteredPages = computed(() => {
                const searchTerm = magazinSearch.value.toLowerCase();
                if (searchTerm?.length > 2) {
                    /* eslint-disable-next-line */
                    const searchInPosts = posts.value.filter((post) => {
                        const postsSearchContent = `${post.title?.rendered.toLowerCase()}
                        ${post.content?.rendered.toLowerCase()}`;

                        if (postsSearchContent.includes(searchTerm)) {
                            return true;
                        }

                        return false;
                    });

                    return pages.value.filter((page) => {
                        let pageInSearch = false;
                        const pagesSearchContent = `${page.title?.rendered.toLowerCase()}
     ${page.content?.rendered.toLowerCase()}`;

                        if ((pagesSearchContent.includes(searchTerm)
                            && page.acf.magazineStart === true)
                            || (searchInPosts.find((post) => post.magazinecat.includes(page.acf.magazineCat)
                            && page.acf.magazineStart === true))
                        ) {
                            pageInSearch = true;
                        }

                        return pageInSearch;
                    });
                }
                return false;
            });

            onMounted(async() => {
                await store.dispatch('setPosts');
                await store.dispatch('setPages');

                ready.value = true;
            });

            const setUrlfromFirstResult = () => {
                if (filteredPages.value.length > 0) {
                    const firstResult = filteredPages.value[0];

                    window.location.href = firstResult.permalink;
                }
            };

            return {
                pages,
                ready,
                magazinSearch,
                filteredPages,
                setUrlfromFirstResult,
            };
        }
    };
    Vue.createApp(kioskSearch).use(store).mount('#kioskSearch');
}
